import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FolderOpen, List, Grid, Search, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import PortfolioClient from '../data/api/PortfolioClient';
import { Learner } from '../../learnerControl/data/interfaces/LearnerInterfaces';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../../../shared/components/Table/Table';
import Loader from '../../../shared/components/Loader';
import Modal from '../../../shared/components/Modal';
import LearnerClient from '../../learnerControl/data/api/LearnerClient';
import '../styles/PortfolioStyles.css';
import '../../learnerControl/styles/LearnerStyles.css';

const PAGE_LIMIT = 10;

const getInitials = (first?: string, last?: string) => {
  return ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || '??';
};

const getStatusClass = (status?: string) => {
  const s = (status || '').toLowerCase();
  if (s === 'active') return 'active';
  if (s === 'approved') return 'approved';
  if (s === 'draft') return 'draft';
  if (s === 'inactive') return 'inactive';
  return 'default';
};

interface FolderCardProps {
  learner: Learner;
  colorIndex?: number;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isMenuOpen?: boolean;
  onMenuToggle?: (open: boolean) => void;
}

const binderColors = [
  { front: '#ffe4e6', back: '#fecdd3', avatar: '#e11d48', text: '#881337', subtext: '#be123c' }, // Rose
  { front: '#dbeafe', back: '#bfdbfe', avatar: '#2563eb', text: '#1e3a8a', subtext: '#1d4ed8' }, // Blue
  { front: '#d1fae5', back: '#a7f3d0', avatar: '#059669', text: '#064e3b', subtext: '#047857' }, // Emerald
  { front: '#fef3c7', back: '#fde68a', avatar: '#d97706', text: '#78350f', subtext: '#b45309' }, // Amber
  { front: '#ede9fe', back: '#ddd6fe', avatar: '#7c3aed', text: '#4c1d95', subtext: '#6d28d9' }, // Violet
  { front: '#fce7f3', back: '#fbcfe8', avatar: '#db2777', text: '#831843', subtext: '#be185d' }, // Pink
  { front: '#ccfbf1', back: '#99f6e4', avatar: '#0d9488', text: '#134e4a', subtext: '#0f766e' }, // Teal
  { front: '#ffedd5', back: '#fed7aa', avatar: '#ea580c', text: '#7c2d12', subtext: '#c2410c' }, // Orange
];

const FolderCard: React.FC<FolderCardProps> = ({ learner, colorIndex = 0, onClick, onEdit, onDelete, isMenuOpen, onMenuToggle }) => {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const menuOpen = isMenuOpen !== undefined ? isMenuOpen : internalMenuOpen;
  const setMenuOpen = (open: boolean) => {
    if (onMenuToggle) onMenuToggle(open);
    else setInternalMenuOpen(open);
  };
  const init1 = learner.first_name ? learner.first_name[0].toUpperCase() : '';
  const init2 = learner.last_name ? learner.last_name[0].toUpperCase() : '';
  const fullName = `${learner.first_name || ''} ${learner.last_name || ''}`.trim();
  const statusStr = (learner.status || 'applicant').toUpperCase();
  const isActionRequired = (learner.status || '').toLowerCase() === 'action required';

  const isInactive = statusStr.toLowerCase() === 'inactive';
  const color = binderColors[colorIndex % binderColors.length];

  return (
    <div
      className="binder-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      style={{
        '--binder-front-bg': color.front,
        '--binder-back-bg': color.back,
        '--binder-avatar-bg': color.avatar,
        '--binder-text-color': color.text,
        '--binder-subtext-color': color.subtext,
      } as React.CSSProperties}
    >

      {/* Back Cover (Right edge showing) */}
      <div className="binder-back"></div>

      {/* Paper Pages */}
      <div className="binder-pages">
        <div className="binder-page page-1"></div>
        <div className="binder-page page-2"></div>
        <div className="binder-page page-3"></div>
      </div>

      {/* Colorful Index Tabs */}
      <div className="binder-tabs">
        <div className="b-tab tab-blue"></div>
        <div className="b-tab tab-green"></div>
        <div className="b-tab tab-pink"></div>
        <div className="b-tab tab-cream"></div>
        <div className="b-tab tab-grey"></div>
      </div>

      {/* Front Cover */}
      <div className="binder-front">
        <div className="binder-spine"></div>

        {/* Profile Content */}
        <div className="binder-content" style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }} className="folder-menu-wrapper">
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: color.subtext, padding: '4px', borderRadius: '4px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>
            </button>
            {menuOpen && (
              <div
                className="folder-dropdown"
                  style={{
                    position: 'absolute', right: 0, top: '100%', marginTop: '4px',
                    background: '#fff', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: '4px', zIndex: 100, minWidth: '120px', border: '1px solid #e2e8f0'
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <button onClick={() => { setMenuOpen(false); onClick(); }} style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#334155', borderRadius: '4px' }} onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    View
                  </button>
                  {onEdit && (
                    <button onClick={() => { setMenuOpen(false); onEdit(); }} style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#334155', borderRadius: '4px' }} onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      Edit
                    </button>
                  )}
                  {onDelete && (
                    <button onClick={() => { setMenuOpen(false); onDelete(); }} style={{ width: '100%', textAlign: 'left', padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: '#ef4444', borderRadius: '4px' }} onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      Delete
                    </button>
                  )}
              </div>
            )}
          </div>
          <div className="binder-avatar">{init1}{init2}</div>
          <div className="binder-info">
            <h3 className="binder-name" title={fullName}>{fullName}</h3>
            <p className="binder-role">{learner.job_categories?.[0] || 'Applicant'}</p>
          </div>

          <div className="binder-email" title={(learner.email_address && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(learner.email_address)) ? learner.email_address : ""}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
            <span>{(learner.email_address && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(learner.email_address)) ? learner.email_address : "-"}</span>
          </div>

          <div className="binder-footer">
            {isActionRequired ? (
              <span
                className="custom-tooltip"
                style={{ cursor: 'help', fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif' }}
                data-tooltip={`Missing fields: ${[(!learner.first_name || learner.first_name === 'Unknown') ? 'First Name' : null, (!learner.last_name || learner.last_name === 'Unknown') ? 'Last Name' : null, (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(learner.email_address || '')) ? 'Email' : null].filter(Boolean).join(', ')}. Edit to fix.`}
              >
                <span className="binder-hint" style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <path d="M12 16v-4M12 8h.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Action Required
                </span>
              </span>
            ) : (
              <span className="binder-hint">tap to open →</span>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

interface GroupFolderCardProps {
  id: string;
  name: string;
  count: number;
  colorIndex: number;
  onClick: () => void;
}

const GroupFolderCard: React.FC<GroupFolderCardProps> = ({ name, count, colorIndex, onClick }) => {
  const color = binderColors[colorIndex % binderColors.length];

  return (
    <div
      className="binder-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      style={{
        '--binder-front-bg': color.front,
        '--binder-back-bg': color.back,
        '--binder-avatar-bg': color.avatar,
        '--binder-text-color': color.text,
        '--binder-subtext-color': color.subtext,
      } as React.CSSProperties}
    >
      {/* Back Cover (Right edge showing) */}
      <div className="binder-back"></div>

      {/* Paper Pages */}
      <div className="binder-pages">
        <div className="binder-page page-1"></div>
        <div className="binder-page page-2"></div>
        <div className="binder-page page-3"></div>
      </div>

      {/* Colorful Index Tabs */}
      <div className="binder-tabs">
        <div className="b-tab tab-blue"></div>
        <div className="b-tab tab-green"></div>
        <div className="b-tab tab-pink"></div>
        <div className="b-tab tab-cream"></div>
        <div className="b-tab tab-grey"></div>
      </div>

      {/* Front Cover */}
      <div className="binder-front">
        <div className="binder-spine"></div>

        {/* Profile Content */}
        <div className="binder-content">
          <div className="binder-avatar"><FolderOpen size={20} color={color.text} /></div>
          <div className="binder-info">
            <h3 className="binder-name" style={{ color: color.text, textTransform: 'capitalize' }}>{name}</h3>
            <p className="binder-role" style={{ color: color.subtext, opacity: 0.85 }}>{count} Applicant{count !== 1 ? 's' : ''}</p>
          </div>

          <div className="binder-footer" style={{ marginTop: 'auto' }}>
            <span className="binder-hint" style={{ color: color.subtext, opacity: 0.8 }}>tap to open group →</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const GroupFolderCardV2: React.FC<GroupFolderCardProps> = ({ name, count, colorIndex, onClick }) => {
  const colorsV2 = [
    { bg: '#ffe4e6', tab: '#fecdd3', text: '#be123c' }, // Rose
    { bg: '#dbeafe', tab: '#bfdbfe', text: '#1d4ed8' }, // Blue
    { bg: '#d1fae5', tab: '#a7f3d0', text: '#047857' }, // Emerald
    { bg: '#fef3c7', tab: '#fde68a', text: '#b45309' }, // Amber
    { bg: '#ede9fe', tab: '#ddd6fe', text: '#6d28d9' }, // Violet
    { bg: '#fce7f3', tab: '#fbcfe8', text: '#be185d' }, // Pink
    { bg: '#ccfbf1', tab: '#99f6e4', text: '#0f766e' }, // Teal
    { bg: '#ffedd5', tab: '#fed7aa', text: '#c2410c' }, // Orange
  ];
  const color = colorsV2[colorIndex % colorsV2.length];

  return (
    <div
      className={`folder-v2-card ${count === 0 ? 'empty' : ''}`}
      onClick={count > 0 ? onClick : undefined}
      role="button"
      tabIndex={count > 0 ? 0 : -1}
      onKeyDown={e => e.key === 'Enter' && count > 0 && onClick()}
      style={{
        '--folder-v2-bg': color.tab,
        '--folder-v2-tab-bg': color.bg,
        '--folder-v2-text': color.text,
        cursor: count > 0 ? 'pointer' : 'default',
        opacity: count > 0 ? 1 : 0.6
      } as React.CSSProperties}
    >
      <div className="folder-v2-tab"></div>
      <svg className="folder-v2-tab-curve" viewBox="0 0 20 20" preserveAspectRatio="none">
        <path d="M0,0 C10,0 10,20 20,20 L0,20 Z" fill="var(--folder-v2-tab-bg)" />
      </svg>
      <div className="folder-v2-back"></div>
      <div className="folder-v2-paper folder-v2-paper-1">
        <div className="paper-line" style={{ width: '60%' }}></div>
        <div className="paper-line" style={{ width: '80%' }}></div>
      </div>
      <div className="folder-v2-paper folder-v2-paper-2">
        <div className="paper-line" style={{ width: '70%' }}></div>
        <div className="paper-line" style={{ width: '90%' }}></div>
        <div className="paper-line" style={{ width: '40%' }}></div>
      </div>
      <div className="folder-v2-front">
        <h3 className="folder-v2-title" title={name}>{name}</h3>
        <div className="folder-v2-count-wrapper">
          <span className="folder-v2-count-num">{count}</span>
          <span className="folder-v2-count-label">Applicant{count !== 1 ? 's' : ''}</span>
        </div>
        <div className="folder-v2-divider"></div>
        <div className="folder-v2-footer" style={{ color: count === 0 ? '#94a3b8' : undefined }}>{count > 0 ? 'tap to open group \u2192' : 'No applicants yet'}</div>
      </div>
    </div>
  );
};

const GroupFolderCardV3: React.FC<GroupFolderCardProps> = ({ name, count, colorIndex, onClick }) => {
  const colorsV3 = [
    { main: '#93c5fd', light: '#bfdbfe', tint: 'rgba(147, 197, 253, 0.15)', border: 'rgba(147, 197, 253, 0.5)' }, // Blue
    { main: '#6ee7b7', light: '#a7f3d0', tint: 'rgba(110, 231, 183, 0.15)', border: 'rgba(110, 231, 183, 0.5)' }, // Green
    { main: '#fcd34d', light: '#fde68a', tint: 'rgba(252, 211, 77, 0.15)', border: 'rgba(252, 211, 77, 0.5)' }, // Amber
    { main: '#c4b5fd', light: '#ddd6fe', tint: 'rgba(196, 181, 253, 0.15)', border: 'rgba(196, 181, 253, 0.5)' }, // Violet
    { main: '#f9a8d4', light: '#fbcfe8', tint: 'rgba(249, 168, 212, 0.15)', border: 'rgba(249, 168, 212, 0.5)' }, // Pink
    { main: '#93c5fd', light: '#bfdbfe', tint: 'rgba(147, 197, 253, 0.15)', border: 'rgba(147, 197, 253, 0.5)' }, // Blue
    { main: '#fdba74', light: '#fed7aa', tint: 'rgba(253, 186, 116, 0.15)', border: 'rgba(253, 186, 116, 0.5)' }, // Orange
    { main: '#5eead4', light: '#99f6e4', tint: 'rgba(94, 234, 212, 0.15)', border: 'rgba(94, 234, 212, 0.5)' }, // Teal
  ];
  const color = colorsV3[colorIndex % colorsV3.length];

  return (
    <div
      className={`folder-v3-card ${count === 0 ? 'empty' : ''}`}
      onClick={count > 0 ? onClick : undefined}
      role="button"
      tabIndex={count > 0 ? 0 : -1}
      onKeyDown={e => e.key === 'Enter' && count > 0 && onClick()}
      style={{
        '--card-tint': color.tint,
        '--card-border': color.border,
        cursor: count > 0 ? 'pointer' : 'default',
        opacity: count > 0 ? 1 : 0.6
      } as React.CSSProperties}
    >
      <div className="folder-v3-icon">
        <svg width="40" height="32" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 4C0 1.79086 1.79086 0 4 0H14.5C15.5609 0 16.5783 0.421427 17.3284 1.17157L19.5 3.34315C20.2501 4.09329 21.2675 4.51472 22.3284 4.51472H36C38.2091 4.51472 40 6.30558 40 8.51472V28C40 30.2091 38.2091 32 36 32H4C1.79086 32 0 30.2091 0 28V4Z" fill={color.light} />
          <path d="M0 10.5C0 8.29086 1.79086 6.5 4 6.5H36C38.2091 6.5 40 8.29086 40 10.5V28C40 30.2091 38.2091 32 36 32H4C1.79086 32 0 30.2091 0 28V10.5Z" fill={color.main} />
        </svg>
      </div>
      <div className="folder-v3-content">
        <h3 className="folder-v3-title" title={name}>{name}</h3>
        <p className="folder-v3-count">{count} applicant{count !== 1 ? 's' : ''}</p>
      </div>
      <button className="folder-v3-menu" onClick={e => e.stopPropagation()}>
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" /></svg>
      </button>
    </div>
  );
};

const PortfolioListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<'folder' | 'table'>(location.state?.viewMode || 'folder');

  // 2-level state
  const [groupBy, setGroupBy] = useState<'status' | 'job_category' | 'assigned_to'>(location.state?.groupBy || 'status');
  const [currentGroup, setCurrentGroup] = useState<{ id: string, name: string } | null>(location.state?.currentGroup || null);
  const [groups, setGroups] = useState<{ id: string, name: string, count: number }[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [jobCategoryFilter, setJobCategoryFilter] = useState('');

  const [learners, setLearners] = useState<Learner[]>([]);
  const [loading, setLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [folderUIVersion, setFolderUIVersion] = useState<'v2' | 'v3'>('v3');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [page, setPage] = useState<number>(Number(location.state?.page) || 1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalFilteredRecords, setTotalFilteredRecords] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [showAddActions, setShowAddActions] = useState(false);
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const addActionsRef = React.useRef<HTMLDivElement>(null);
  const groupByRef = React.useRef<HTMLDivElement>(null);

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [deleteModal, setDeleteModal] = useState<Learner | null>(null);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    const handleGlobalClick = () => setOpenMenuId(null);
    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(learners.filter(l => l.email_address).map(l => l.email_address!)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (email: string, checked: boolean) => {
    const newSet = new Set(selectedRows);
    if (checked) newSet.add(email);
    else newSet.delete(email);
    setSelectedRows(newSet);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal?.email_address) return;
    setDeleting(true);
    try {
      await LearnerClient.delete(deleteModal.email_address);
      setDeleteModal(null);
      fetchLearners();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleting(true);
    try {
      await LearnerClient.bulkDeleteUsers(Array.from(selectedRows));
      setBulkDeleteModal(false);
      setSelectedRows(new Set());
      fetchLearners();
    } catch (err) {
      console.error(err);
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
      if (addActionsRef.current && !addActionsRef.current.contains(event.target as Node)) {
        setShowAddActions(false);
      }
      if (groupByRef.current && !groupByRef.current.contains(event.target as Node)) {
        setShowGroupByDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const res = await PortfolioClient.getGroups({
        group_by: groupBy,
        name: nameFilter || undefined,
        status: groupBy === 'job_category' ? statusFilter || undefined : undefined,
        job_opportunity_id: groupBy === 'status' ? jobCategoryFilter || undefined : undefined
      });
      setGroups(res.groups || []);
    } catch {
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [groupBy, nameFilter, statusFilter, jobCategoryFilter]);

  useEffect(() => {
    if (!currentGroup) {
      const t = setTimeout(fetchGroups, 300);
      return () => clearTimeout(t);
    }
  }, [fetchGroups, currentGroup]);

  const fetchLearners = useCallback(async () => {
    if (!currentGroup) return; // Only fetch if we drilled down
    setLoading(true);
    try {
      const res = await PortfolioClient.list({
        page_number: page,
        limit: PAGE_LIMIT,
        name: nameFilter || undefined,
        status: groupBy === 'status' ? currentGroup.id : (statusFilter || undefined),
        job_opportunity_id: groupBy === 'job_category' ? currentGroup.id : (jobCategoryFilter || undefined),
        assigned_to: groupBy === 'assigned_to' ? currentGroup.id : undefined,
      });
      setLearners(res.records || []);
      setTotalFilteredRecords(res.total_filtered_records ?? (res.total_filtered_records || res.no_of_records || 0));
      setTotalRecords(res.total_filtered_records || 0);
      if (res.status_counts) {
        setStatusCounts(res.status_counts);
      }
    } catch {
      setLearners([]);
    } finally {
      setLoading(false);
    }
  }, [page, nameFilter, statusFilter, jobCategoryFilter, currentGroup, groupBy]);

  useEffect(() => {
    if (currentGroup) {
      const t = setTimeout(fetchLearners, 300);
      return () => clearTimeout(t);
    }
  }, [fetchLearners, currentGroup]);

  const openPortfolio = (id?: string, action?: 'edit') => {
    if (!id) return;
    const actionParam = action ? `&action=${action}` : '';
    navigate(`/dashboard/portfolio/detail?candidate_id=${encodeURIComponent(id)}${actionParam}`, { state: { currentGroup, groupBy, page, viewMode } });
  };

  const totalPages = Math.ceil(totalFilteredRecords / PAGE_LIMIT);

  return (
    <>
      <div className="learner-page">

        {/* ── Hero ── */}
        <div className="lp-hero" style={{ flexDirection: "column", gap: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
          <div className="lp-hero-left">
            <h1>Applicant Portfolios</h1>
            <span className="lp-hero-chip">Browse applicant interviews, job matches & documents in one place.</span>
          </div>

          <div className="lp-hero-right" style={{ gap: "12px", display: "flex", alignItems: "center" }}>
            <div
              ref={addActionsRef}
              style={{ position: 'relative' }}
            >
              {/* Green glassy + button */}
              <button
                onClick={() => setShowAddActions(!showAddActions)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '9px 18px', borderRadius: '999px',
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(16,185,129,0.12))',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(34,197,94,0.45)',
                  boxShadow: '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.4)',
                  color: '#15803d', fontWeight: 700, fontSize: '13.5px',
                  cursor: 'pointer', transition: 'all 0.2s ease',
                  letterSpacing: '0.01em',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34,197,94,0.28), rgba(16,185,129,0.2))';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,197,94,0.32), inset 0 1px 0 rgba(255,255,255,0.5)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(34,197,94,0.18), rgba(16,185,129,0.12))';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(34,197,94,0.2), inset 0 1px 0 rgba(255,255,255,0.4)';
                  e.currentTarget.style.transform = 'none';
                }}
                title="Add Applicant / Upload Resume"
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  boxShadow: '0 2px 6px rgba(22,163,74,0.4)',
                  color: '#fff', flexShrink: 0,
                }}>
                  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                </span>
                Add New
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"
                  style={{ opacity: 0.6, transform: showAddActions ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showAddActions && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border: '1px solid rgba(34,197,94,0.25)',
                  borderRadius: '14px',
                  boxShadow: '0 12px 32px rgba(0,0,0,0.1), 0 4px 12px rgba(34,197,94,0.12)',
                  minWidth: '200px', zIndex: 20,
                  display: 'flex', flexDirection: 'column', overflow: 'hidden',
                  padding: '6px',
                }}>
                  <button
                    onClick={() => { setShowAddActions(false); navigate("/dashboard/learners/add", { state: { returnTo: "/dashboard/portfolio" } }); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px',
                      background: 'transparent', border: 'none', borderRadius: '10px',
                      fontSize: '13px', fontWeight: 500, color: '#15803d', cursor: 'pointer', textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(34,197,94,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ width: 30, height: 30, borderRadius: '8px', background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="15" height="15" fill="none" stroke="#16a34a" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </span>
                    <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '13.5px' }}>
                      Add Applicant
                    </span>
                  </button>
                  <button
                    onClick={() => { setShowAddActions(false); navigate("/dashboard/learners/upload", { state: { returnTo: "/dashboard/portfolio" } }); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 14px',
                      background: 'transparent', border: 'none', borderRadius: '10px',
                      fontSize: '13px', fontWeight: 500, color: '#15803d', cursor: 'pointer', textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(34,197,94,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ width: 30, height: 30, borderRadius: '8px', background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="15" height="15" fill="none" stroke="#16a34a" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </span>
                    <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '13.5px' }}>
                      Upload Resume
                    </span>
                  </button>
                </div>
              )}
            </div>

            {!!currentGroup && (
              <div style={{ display: 'flex', backgroundColor: '#f0fcf6', border: '1px solid #d1fae5', borderRadius: '999px', padding: '4px', gap: '4px' }}>
                <button
                  onClick={() => setViewMode('folder')}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: viewMode === 'folder' ? '#2b6a50' : 'transparent', border: 'none', borderRadius: '999px', boxShadow: viewMode === 'folder' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none', color: viewMode === 'folder' ? '#ffffff' : '#64748b', fontWeight: 600, fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { if (viewMode !== 'folder') e.currentTarget.style.color = '#334155'; }}
                  onMouseLeave={(e) => { if (viewMode !== 'folder') e.currentTarget.style.color = '#64748b'; }}
                >
                  <FolderOpen size={14} /> Grid
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: viewMode === 'table' ? '#2b6a50' : 'transparent', border: 'none', borderRadius: '999px', boxShadow: viewMode === 'table' ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none', color: viewMode === 'table' ? '#ffffff' : '#64748b', fontWeight: 600, fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease' }}
                  onMouseEnter={(e) => { if (viewMode !== 'table') e.currentTarget.style.color = '#334155'; }}
                  onMouseLeave={(e) => { if (viewMode !== 'table') e.currentTarget.style.color = '#64748b'; }}
                >
                  <List size={14} /> Table
                </button>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── Table / Grid Panel ── */}
      <div className="lp-table-panel" style={{ marginTop: "16px" }}>

        {/* ── Toolbar Strip ── */}
        <div style={{
          padding: "10px 20px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: "1px solid #e2e8f0",
          background: "#f8fafc", gap: "12px", flexWrap: "wrap"
        }}>
          {/* LEFT: total + stat pills + clear + refresh */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingRight: "4px" }}>
              {currentGroup && (
                <button
                  onClick={() => { setCurrentGroup(null); setPage(1); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '999px',
                    color: '#475569',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginRight: '8px',
                    boxShadow: '0 2px 4px rgba(15, 23, 42, 0.05)'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#0f172a';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(15, 23, 42, 0.08)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.color = '#475569';
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(15, 23, 42, 0.05)';
                  }}
                >
                  <ChevronLeft size={16} strokeWidth={2.5} /> Back to Folders
                </button>
              )}
              <div style={{ width: 36, height: 36, borderRadius: "10px", background: "#f0fdf4", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <FolderOpen size={18} />
              </div>
              <span style={{ fontSize: "13px", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>{currentGroup ? currentGroup.name : 'Total Groups'}</span>
              <span style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginLeft: "2px" }}>{currentGroup ? totalRecords : groups.length}</span>
            </div>

            <div style={{ width: 1, height: 20, background: "#e2e8f0" }} />



            <button
              onClick={() => { setPage(1); if (currentGroup) fetchLearners(); else fetchGroups(); }}
              title="Refresh"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", background: "transparent", border: "none", borderRadius: "6px", color: "#64748b", cursor: "pointer", transition: "all 0.2s" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#334155"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#64748b"; }}
            >
              <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>

            {!currentGroup && (
              <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px', marginLeft: '4px' }}>
                {(['v2', 'v3'] as const).map(v => (
                  <button
                    key={v}
                    onClick={() => setFolderUIVersion(v)}
                    style={{
                      background: folderUIVersion === v ? '#fff' : 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      fontWeight: folderUIVersion === v ? 600 : 500,
                      color: folderUIVersion === v ? '#0f172a' : '#64748b',
                      cursor: 'pointer',
                      boxShadow: folderUIVersion === v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      transition: 'all 0.2s',
                    }}
                  >
                    {v === 'v2' ? 'Folder View' : 'Card View'}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: group by + search + pagination */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {/* Group By Toggle */}
            {!currentGroup && (
              <div ref={groupByRef} style={{
                position: 'relative',
                zIndex: 40,
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'linear-gradient(135deg, rgba(240, 252, 246, 0.7) 0%, rgba(209, 250, 229, 0.5) 100%)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(43, 106, 80, 0.15)',
                borderRadius: '999px',
                padding: '4px 4px 4px 16px',
                boxShadow: '0 2px 10px rgba(43,106,80,0.05)'
              }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#2b6a50' }}>
                  Group by: <span style={{ color: '#0f172a' }}>{groupBy === 'status' ? 'Status' : groupBy === 'job_category' ? 'Job Role' : 'Assigned To'}</span>
                </span>
                <button
                  onClick={() => setShowGroupByDropdown(!showGroupByDropdown)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px',
                    background: 'rgba(255, 255, 255, 0.9)', border: '1px solid rgba(43, 106, 80, 0.1)', borderRadius: '50%',
                    color: '#2b6a50', cursor: 'pointer', transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(43,106,80,0.05)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                {showGroupByDropdown && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 252, 246, 0.85) 100%)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(43, 106, 80, 0.15)',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(43,106,80,0.15)',
                    minWidth: '160px', zIndex: 30,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    padding: '6px',
                  }}>
                    <button
                      onClick={() => { setGroupBy('status'); setShowGroupByDropdown(false); setCurrentGroup(null); setPage(1); }}
                      style={{
                        padding: '10px 14px', background: groupBy === 'status' ? 'rgba(43, 106, 80, 0.08)' : 'transparent', border: 'none', borderRadius: '10px',
                        fontSize: '13px', fontWeight: groupBy === 'status' ? 600 : 500, color: groupBy === 'status' ? '#2b6a50' : '#475569', cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => { if (groupBy !== 'status') e.currentTarget.style.background = 'rgba(43, 106, 80, 0.04)'; }}
                      onMouseLeave={e => { if (groupBy !== 'status') e.currentTarget.style.background = 'transparent'; }}
                    >
                      Status
                    </button>
                    <button
                      onClick={() => { setGroupBy('job_category'); setShowGroupByDropdown(false); setCurrentGroup(null); setPage(1); }}
                      style={{
                        padding: '10px 14px', background: groupBy === 'job_category' ? 'rgba(43, 106, 80, 0.08)' : 'transparent', border: 'none', borderRadius: '10px',
                        fontSize: '13px', fontWeight: groupBy === 'job_category' ? 600 : 500, color: groupBy === 'job_category' ? '#2b6a50' : '#475569', cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => { if (groupBy !== 'job_category') e.currentTarget.style.background = 'rgba(43, 106, 80, 0.04)'; }}
                      onMouseLeave={e => { if (groupBy !== 'job_category') e.currentTarget.style.background = 'transparent'; }}
                    >
                      Job Role
                    </button>
                    <button
                      onClick={() => { setGroupBy('assigned_to'); setShowGroupByDropdown(false); setCurrentGroup(null); setPage(1); }}
                      style={{
                        padding: '10px 14px', background: groupBy === 'assigned_to' ? 'rgba(43, 106, 80, 0.08)' : 'transparent', border: 'none', borderRadius: '10px',
                        fontSize: '13px', fontWeight: groupBy === 'assigned_to' ? 600 : 500, color: groupBy === 'assigned_to' ? '#2b6a50' : '#475569', cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => { if (groupBy !== 'assigned_to') e.currentTarget.style.background = 'rgba(43, 106, 80, 0.04)'; }}
                      onMouseLeave={e => { if (groupBy !== 'assigned_to') e.currentTarget.style.background = 'transparent'; }}
                    >
                      Assigned To
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* search */}
            <div style={{ height: 34, width: 220, background: "#fff", position: "relative", display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <svg width="13" height="13" fill="none" stroke="#94a3b8" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                placeholder="Search name or email..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    setNameFilter(searchInput);
                    setPage(1);
                  }
                }}
                style={{ border: 'none', outline: 'none', background: 'transparent', paddingLeft: '7px', width: '100%', fontSize: '0.82rem' }}
              />
              {searchInput && (
                <button
                  style={{ position: "absolute", right: "7px", top: "50%", transform: "translateY(-50%)", border: "none", background: "#f1f5f9", cursor: "pointer", color: "#64748b", width: "16px", height: "16px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}
                  onClick={() => { setSearchInput(""); setNameFilter(""); setPage(1); }}
                >
                  <svg width="9" height="9" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>



            {/* pagination */}
            {!loading && totalPages > 1 && (
              <div style={{
                display: 'inline-flex', alignItems: 'center',
                background: 'rgba(220,252,231,0.5)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                borderRadius: '999px',
                padding: '3px',
                gap: '1px',
                border: '1px solid rgba(34,197,94,0.25)',
                boxShadow: '0 2px 10px rgba(34,197,94,0.1), inset 0 1px 0 rgba(255,255,255,0.6)',
              }}>
                {/* Prev */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'transparent', border: 'none',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    color: page === 1 ? '#a7f3d0' : '#15803d',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (page !== 1) { e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <ChevronLeft size={14} />
                </button>

                {(() => {
                  const pages: (number | '...')[] = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (page > 3) pages.push('...');
                    const start = Math.max(2, page - 1);
                    const end = Math.min(totalPages - 1, page + 1);
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages.map((p, idx) =>
                    p === '...' ? (
                      <span key={`ellipsis-${idx}`} style={{ width: 28, textAlign: 'center', fontSize: '12px', color: '#6ee7b7', lineHeight: '28px' }}>…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p as number)}
                        style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: page === p
                            ? 'linear-gradient(135deg, #16a34a, #15803d)'
                            : 'transparent',
                          color: page === p ? '#fff' : '#166534',
                          border: 'none',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12.5px', fontWeight: page === p ? 700 : 500,
                          cursor: 'pointer', transition: 'all 0.15s',
                          boxShadow: page === p ? '0 3px 10px rgba(22,163,74,0.45)' : 'none',
                        }}
                        onMouseEnter={e => { if (page !== p) e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; }}
                        onMouseLeave={e => { if (page !== p) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {p}
                      </button>
                    )
                  );
                })()}

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: 'transparent', border: 'none',
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                    color: page === totalPages ? '#a7f3d0' : '#15803d',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (page !== totalPages) { e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>


        <div className="lp-table-scroll-area">
          {!currentGroup ? (
            <>
              <Loader isLoading={loadingGroups} message="Loading groups..." />
              {!loadingGroups && (
                groups.length === 0 ? (
                  <div className="table-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#94a3b8', gap: '12px' }}>
                    <FolderOpen size={48} color="#cbd5e1" />
                    <p>No Groups found</p>
                  </div>
                ) : (
                  <div style={{ padding: '16px' }}>
                    <div className="folder-grid">
                      {groups.map((g, i) => {
                        const props = {
                          key: g.id,
                          id: g.id,
                          name: g.name,
                          count: g.count,
                          colorIndex: i,
                          onClick: () => { setCurrentGroup({ id: g.id, name: g.name }); setPage(1); }
                        };
                        if (folderUIVersion === 'v2') return <GroupFolderCardV2 {...props} />;
                        return <GroupFolderCardV3 {...props} />;
                      })}
                    </div>
                  </div>
                )
              )}
            </>
          ) : (
            <>
              <Loader isLoading={loading} message={`Loading applicants in ${currentGroup.name}...`} />
              {!loading && (
                learners.length === 0 ? (
                  <div className="table-empty" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#94a3b8', gap: '12px' }}>
                    <FolderOpen size={48} color="#cbd5e1" />
                    <p>No Applicants found</p>
                  </div>
                ) : viewMode === 'folder' ? (
                  <div style={{ padding: '16px' }}>
                    <div className="folder-grid">
                      {learners.map((l, index) => (
                        <FolderCard
                          key={l.candidate_id}
                          learner={l}
                          colorIndex={index}
                          onClick={() => openPortfolio(l.candidate_id)}
                          onEdit={() => openPortfolio(l.candidate_id, 'edit')}
                          onDelete={() => setDeleteModal(l)}
                          isMenuOpen={openMenuId === l.candidate_id}
                          onMenuToggle={(open) => setOpenMenuId(open ? l.candidate_id || null : null)}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="lp-table-wrapper">
                    {selectedRows.size > 0 && (
                      <div style={{ padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155' }}>{selectedRows.size} selected</span>
                        <button
                          className="al-delete-btn"
                          disabled={bulkDeleting}
                          onClick={() => setBulkDeleteModal(true)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px',
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#fff', border: 'none', borderRadius: '20px',
                            fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: bulkDeleting ? 0.6 : 1
                          }}
                        >
                          {bulkDeleting ? "Deleting..." : `Delete (${selectedRows.size})`}
                        </button>
                      </div>
                    )}
                    <Table className="learner-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead style={{ width: 40, paddingLeft: 20 }}>
                            <input
                              type="checkbox"
                              className="lp-checkbox"
                              checked={learners.length > 0 && selectedRows.size === learners.length}
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </TableHead>
                          <TableHead>Applicant Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Education</TableHead>
                          <TableHead>Onboarded</TableHead>
                          <TableHead className="actions-header">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {learners.map(l => {
                          const status = (l.status || "").toLowerCase();
                          const isActive = status === "active" || status === "interviewed";
                          const isInactive = status === "inactive" || status === "failed";
                          const isDraft = status === "draft" || status === "action required";
                          const rowBg = isActive ? "rgba(34, 197, 94, 0.08)" : isInactive ? "rgba(148, 163, 184, 0.15)" : isDraft ? "rgba(234, 179, 8, 0.1)" : undefined;

                          return (
                            <TableRow
                              key={l.candidate_id}
                              onClick={() => openPortfolio(l.candidate_id)}
                              style={{ height: '60px', backgroundColor: rowBg, cursor: 'pointer' }}
                            >
                              <TableCell style={{ paddingLeft: 20 }} onClick={e => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="lp-checkbox"
                                  checked={!!l.email_address && selectedRows.has(l.email_address)}
                                  onChange={(e) => {
                                    if (l.email_address) handleSelectRow(l.email_address, e.target.checked);
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="lp-user-cell" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <div className="lp-avatar" style={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.85rem', fontWeight: 600, background: isActive ? "linear-gradient(135deg, #22c55e, #15803d)" : isInactive ? "linear-gradient(135deg, #94a3b8, #475569)" : isDraft ? "linear-gradient(135deg, #facc15, #ca8a04)" : "linear-gradient(135deg, #0ea5e9, #0369a1)" }}>
                                    {getInitials(l.first_name, l.last_name)}
                                  </div>
                                  <span className="lp-user-name" style={{ fontWeight: 600, color: '#0f172a' }}>{l.first_name} {l.last_name}</span>
                                </div>
                              </TableCell>
                              <TableCell style={{ fontSize: '0.875rem', color: '#475569' }}>
                                {(l.email_address && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(l.email_address)) ? l.email_address : "No email on file"}
                              </TableCell>
                              <TableCell>
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
                                  borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700,
                                  background: isActive ? '#dcfce7' : isInactive ? '#f1f5f9' : isDraft ? '#fef9c3' : '#e0f2fe',
                                  color: isActive ? '#15803d' : isInactive ? '#475569' : isDraft ? '#92400e' : '#0284c7'
                                }}>
                                  {(l.status || 'N/A').charAt(0).toUpperCase() + (l.status || 'N/A').slice(1)}
                                </span>
                              </TableCell>
                              <TableCell style={{ fontSize: '0.875rem', color: '#475569' }}>{l.education || '—'}</TableCell>
                              <TableCell style={{ fontSize: '0.875rem', color: '#475569' }}>{l.onboarded ? '✓ Yes' : '—'}</TableCell>
                              <TableCell className="actions-cell" onClick={(e) => e.stopPropagation()}>
                                <div className="actions-row">
                                  {status === 'action required' ? (
                                    <button
                                      className="action-btn custom-tooltip"
                                      style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', cursor: 'not-allowed' }}
                                      onClick={(e) => e.stopPropagation()}
                                      data-tooltip={`Missing fields: ${[(!l.first_name || l.first_name === 'Unknown') ? 'First Name' : null, (!l.last_name || l.last_name === 'Unknown') ? 'Last Name' : null, (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(l.email_address || '')) ? 'Email' : null].filter(Boolean).join(', ')}. Edit to fix.`}
                                    >
                                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                                        <path d="M12 16v-4M12 8h.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </button>
                                  ) : (
                                    <button
                                      className="action-btn view custom-tooltip"
                                      data-tooltip="View Portfolio"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openPortfolio(l.candidate_id);
                                      }}
                                    >
                                      <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    </button>
                                  )}
                                  <button
                                    className="action-btn custom-tooltip"
                                    data-tooltip="Delete Applicant"
                                    onClick={(e) => { e.stopPropagation(); setDeleteModal(l); }}
                                    style={{ color: '#ef4444' }}
                                  >
                                    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </div>

    </div>

      {/* ── Delete Modal ── */}
      <Modal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Delete Applicant"
        width="420px"
      >
        <p>Are you sure you want to delete this applicant? This action cannot be undone.</p>
        {deleteModal && (
          <p style={{ fontWeight: 600, marginTop: 8, color: "#0f172a" }}>
            {deleteModal.first_name} {deleteModal.last_name} ({deleteModal.email_address})
          </p>
        )}
        <div className="modal-actions">
          <button className="btn-cancel1" onClick={() => setDeleteModal(null)}>Cancel</button>
          <button className="btn-danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>

      {/* ── Bulk Delete Modal ── */}
      <Modal
        open={bulkDeleteModal}
        onClose={() => setBulkDeleteModal(false)}
        title="Delete Applicants"
        width="440px"
      >
        <p style={{ color: "#475569", fontSize: "0.95rem", lineHeight: "1.5" }}>
          Are you sure you want to delete <strong>{selectedRows.size}</strong> selected applicant{selectedRows.size === 1 ? '' : 's'}?
          This action will delete them permanently.
        </p>
        <div className="modal-actions">
          <button className="btn-cancel1" onClick={() => setBulkDeleteModal(false)}>Cancel</button>
          <button className="btn-danger" onClick={handleBulkDeleteConfirm} disabled={bulkDeleting} style={{ padding: "8px 16px", background: "#dc2626", color: "#fff", border: "none", borderRadius: "4px", fontWeight: 600, cursor: bulkDeleting ? "not-allowed" : "pointer", opacity: bulkDeleting ? 0.6 : 1 }}>
            {bulkDeleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default PortfolioListPage;
